import React, { useEffect, useRef, useState } from "react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "components/DesignSystem/Carousel/Carousel";
import { BrexHomeCard } from "components/BrexHomeCard/BrexHomeCard";
import { ECLHomeCard } from "components/ECLHomeCard/ECLHomeCard";
import classNames from "classnames";
import { PerksBanner } from "./PerksBanner";

const slides = [
  <BrexHomeCard key="1" />,
  <ECLHomeCard key="2" />,
  <PerksBanner key="3" />,
];

const AUTOPLAY_INTERVAL_MS = 4000;

export const PromotionalCarousel = () => {
  const apiRef = useRef<CarouselApi | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);
  const [isAutoplayPaused, setIsAutoplayPaused] = useState(false);

  useEffect(() => {
    if (isAutoplayPaused) return;

    const interval = setInterval(() => {
      apiRef.current?.scrollNext();
    }, AUTOPLAY_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [apiRef?.current, isAutoplayPaused]);

  useEffect(() => {
    const api = apiRef.current;
    if (!api) return;

    const onSelect = () => setSelectedIndex(api.selectedScrollSnap());
    api.on("select", onSelect);
    onSelect();

    setScrollSnaps(api.scrollSnapList());

    return () => {
      api.off("select", onSelect);
    };
  }, [apiRef?.current]);

  const scrollTo = (index: number) => {
    apiRef.current?.scrollTo(index);
  };

  const pauseAutoplay = () => setIsAutoplayPaused(true);
  const resumeAutoplay = () => setIsAutoplayPaused(false);

  return (
    <div
      className="t-relative t-h-full t-w-full"
      onMouseEnter={pauseAutoplay}
      onMouseLeave={resumeAutoplay}
      onFocusCapture={pauseAutoplay}
      onBlurCapture={resumeAutoplay}
    >
      <Carousel
        opts={{ loop: true }}
        setApi={(api) => (apiRef.current = api)}
        className="t-h-full"
      >
        <CarouselContent
          viewportClassName="t-h-full sm:t-overflow-hidden"
          className="t-h-full sm:t-h-auto"
        >
          {slides.map((Slide, index) => (
            <CarouselItem key={index} className="t-h-full sm:t-h-auto">
              {Slide}
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>

      <div
        className="t-flex t-gap-2 t-absolute t-z-[100] t-bottom-4 t-left-1/2 t-transform -t-translate-x-1/2 
      "
      >
        {scrollSnaps.map((_, index) => (
          <button
            key={index}
            className={classNames(
              "all:unset t-cursor-pointer t-max-w-2 t-max-h-2 t-min-w-2 t-min-h-2 t-rounded-full",
              {
                "t-bg-text-100": index === selectedIndex,
                "t-bg-neutral-20": index !== selectedIndex,
              }
            )}
            onClick={() => scrollTo(index)}
          />
        ))}
      </div>
    </div>
  );
};
